import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteSlider, getSliders } from '../../../../../api/endpoints/cmsSite';
import type { Slider } from '../../../../../api/types/cmsSite';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { SliderFormDialog } from '../SliderFormDialog';

/** List rendered as a plain MUI Table (not @mui/x-data-grid — incompatible with this
 *  project's pinned TypeScript 4.9.5). Confirmed via local-backend swagger + live curl (see
 *  api/types/cmsSite.ts header). Slider is scoped to a parent Site via `site_Id`; this page
 *  is only ever rendered as a tab inside `SiteDetailPage` for one specific site, so `siteId`
 *  comes in as a fixed prop. */
export default function SliderListPage({ siteId }: { siteId: number }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Slider | null>(null);
  const [deleting, setDeleting] = useState<Slider | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'sliders', siteId],
    queryFn: () => getSliders(siteId),
  });
  const rows: Slider[] = useMemo(() => query.data?.sliders ?? [], [query.data]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteSlider(id),
    onSuccess: () => {
      enqueueSnackbar('Slider silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'sliders'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Slider</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Slider
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Görsel Yolu</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={2}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı slider bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.imgPath ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <SliderFormDialog open={createOpen} slider={null} siteId={siteId} onClose={() => setCreateOpen(false)} />
      <SliderFormDialog open={!!editing} slider={editing} siteId={siteId} onClose={() => setEditing(null)} />

      <ConfirmDialog
        open={!!deleting}
        title="Slider'ı sil"
        description="Bu slider görselini silmek istediğinize emin misiniz?"
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
